import PropTypes from "prop-types";
import classNames from "classnames";
import Nav from "react-bootstrap/Nav";
import { useEffect, useState } from "react";
import ReactNavbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Breadcrumbs } from "components/Breadcrumbs";

import { hamburgerIcon, surmountLogoLight } from "assets/icons/index";
import { PlusIcon as PlusIconImage } from "assets/icons";
import {
  FORGOT_PASS_PATH,
  RESET_PASS_PATH,
  SIGNIN_PATH,
  SIGNUP_PATH,
  VERIFY_PHONE_PATH,
  WELCOME_PATH,
} from "features/Auth/routes";
import { collapsedSidebarSelect, hasBannerSelect } from "Layout/slice/selector";
import {
  // isFreeUser,
  isLoggedIn,
  contains,
  // isPremiumUser,
  isMobileDevice,
} from "utils/index";

import { ReferralModal } from "shared";
import { Search } from "./Components/Search";
import { Button } from "components/Button";
import { useNavigation } from "utils/navigate";
import { NavbarContainer } from "./Navbar.styles";
import { setCollapsedSidebar, toggleCreateStrategyModal } from "Layout/slice";
import { loggedInState } from "features/Auth/slice/selector";
import { updateIsLoggedIn } from "features/Auth/slice/thunk";
import { fetchUserFlags } from "Layout/slice/thunk";
import { AccountDropdown } from "./Components/AccountDropdown";
import { BankConnectionAction } from "./Components/BankConnectionAction";
// import { NotificationPanel } from "features/Notification/v2/Panel";
import { PLANS_PATH } from "features/Plans/routes";
import { MAIN_ACCOUNT_PATH } from "features/Account/routes";
import { TransferFundsModal } from "shared/Modal/TransferFundsModal";
import { STRATEGY_V2_PATH } from "features/Strategy/routes";
import { SURMOUNT_ACCOUNT_DETAILS_QUERY_KEY } from "features/Dashboard/hook/useSurmountAccountDetails";

export const Navbar = ({ isAuthPage }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isLoggedInState = useSelector(loggedInState);
  const collapsed = useSelector(collapsedSidebarSelect);
  const hasBanner = useSelector(hasBannerSelect);
  const location = useLocation();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferFundsModal, setShowTransferFundsModal] = useState(false);

  const { navigateToV2Signup, navigateToV2Signin } = useNavigation();

  // const isPremiumPlan = isPremiumUser();
  // const isFreePlan = isFreeUser();

  const isWelcomePath = contains(location.pathname, WELCOME_PATH);
  const showAuthButton =
    !contains(location.pathname, SIGNUP_PATH) &&
    !contains(location.pathname, SIGNIN_PATH) &&
    !contains(location.pathname, FORGOT_PASS_PATH) &&
    !contains(location.pathname, RESET_PASS_PATH) &&
    !contains(location.pathname, VERIFY_PHONE_PATH) &&
    !contains(location.pathname, WELCOME_PATH);

  useEffect(() => {
    handleIsUserLoggedIn();
    dispatch(updateIsLoggedIn());
  }, []);

  // close mobile sidebar when resizing to desktop
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 769px)");
    const onChange = (e) => {
      if (e.matches && collapsed) {
        dispatch(setCollapsedSidebar(false));
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [collapsed, dispatch]);

  const handleSigninVisibility = () => {
    handleIsUserLoggedIn();
    navigateToV2Signin();
  };

  const handleSignupVisibility = () => {
    navigateToV2Signup();
  };

  const handleIsUserLoggedIn = () => {
    dispatch(updateIsLoggedIn());
  };

  const handleCreateStrategyModal = () => {
    if (!isLoggedIn()) {
      navigateToV2Signin();
      return;
    }
    dispatch(toggleCreateStrategyModal(true));
  };

  const toggleCollapsed = () => {
    dispatch(setCollapsedSidebar(!collapsed));
  };

  const toggleInviteModal = () => {
    setShowInviteModal(!showInviteModal);
  };
  const getLogo = () => surmountLogoLight;

  const handleTransferFunds = () => {
    setShowTransferFundsModal(true);
  };

  const handleCloseTransferFundsModal = () => {
    setShowTransferFundsModal(false);
  };

  const handlePlaidSuccess = () => {
    dispatch(fetchUserFlags());
    dispatch(updateIsLoggedIn());
    queryClient.invalidateQueries([SURMOUNT_ACCOUNT_DETAILS_QUERY_KEY]);
  };

  return (
    <NavbarContainer
      variant="dark"
      fixed="top"
      hasbanner={hasBanner.toString()}
      className={classNames({
        "no-sidebar":
          contains(location.pathname, MAIN_ACCOUNT_PATH) ||
          contains(location.pathname, PLANS_PATH),
        "hamburger-icon":
          contains(location.pathname, MAIN_ACCOUNT_PATH) ||
          contains(location.pathname, PLANS_PATH),
      })}
    >
      <Container
        fluid
        className={classNames({
          "justify-content-center": !showAuthButton,
        })}
      >
        <ReactNavbar.Toggle aria-controls="responsive-navbar-nav" />

        {!isLoggedIn() && !isAuthPage && (
          <Link to={"/"} className="link-logo ">
            <img alt="logo" src={getLogo()} className="main-logo" />
          </Link>
        )}

        {isLoggedIn() && !isAuthPage && (
          <img
            src={hamburgerIcon}
            className={classNames("hamburger-icon", {
              "d-none": !showAuthButton,
              "d-block d-xl-none d-lg-none d-md-none": showAuthButton,
            })}
            alt="toggle icon"
            onClick={toggleCollapsed}
          />
        )}

        {!showAuthButton ||
          (!isWelcomePath && (
            <ReactNavbar.Collapse
              id="responsive-navbar-nav"
              className="justify-content-end"
            >
              <Breadcrumbs />

              <Nav>
                {!isMobileDevice() &&
                  (location.pathname === "/" ||
                    location.pathname.startsWith("/dashboard")) && (
                    <>
                      {isLoggedInState && (
                        <div className="logged-in-options">
                          <BankConnectionAction
                            handlePlaidSuccess={handlePlaidSuccess}
                            handleTransferFunds={handleTransferFunds}
                          />
                          <Search id="desktop-search" />

                          {/* {(isPremiumPlan || isFreePlan) && (
                            <NotificationPanel />
                          )} */}
                        </div>
                      )}
                    </>
                  )}

                {/* New Strategy Button */}
                {location.pathname === STRATEGY_V2_PATH && (
                  <Button size="small" onClick={handleCreateStrategyModal}>
                    <PlusIconImage stroke="white" />
                    <div className="btn-text">New strategy</div>
                  </Button>
                )}

                {isMobileDevice() && location.pathname !== STRATEGY_V2_PATH && (
                  <>
                    {isLoggedIn() ? (
                      <div className="search-wrapper">
                        <div className="search-subwrapper logged-in-options">
                          {/* Mobile view bank connection action */}
                          <BankConnectionAction
                            handlePlaidSuccess={handlePlaidSuccess}
                            handleTransferFunds={handleTransferFunds}
                          />
                          {/* <Search id="mobile-search" />
                          {(isPremiumPlan || isFreePlan) && (
                            <NotificationPanel />
                          )} */}
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          size="small"
                          className="signin-btn"
                          onClick={handleSigninVisibility}
                        >
                          Sign In
                        </Button>
                        <Button
                          size="small"
                          className="signup-btn"
                          onClick={handleSignupVisibility}
                        >
                          Sign Up
                        </Button>{" "}
                      </>
                    )}
                  </>
                )}

                {/* User Dropdown */}
                {isLoggedInState && <AccountDropdown />}

                {/* Not loggedin options */}
                {!isLoggedInState && showAuthButton && (
                  <div className="desktop-view">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={handleSigninVisibility}
                    >
                      Sign In
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={handleSignupVisibility}
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </Nav>
            </ReactNavbar.Collapse>
          ))}
      </Container>
      {showInviteModal && <ReferralModal onHide={toggleInviteModal} />}
      <TransferFundsModal
        show={showTransferFundsModal}
        onHide={handleCloseTransferFundsModal}
      />
    </NavbarContainer>
  );
};

Navbar.propTypes = {
  isAuthPage: PropTypes.bool,
};
